import React from 'react';
import { CircleAlert, CircleCheck, CircleDashed, CircleX, Globe, RefreshCw } from 'lucide-react';
import { Button, Card, Pill, SectionHeader, Skeleton } from '../../../components/ui';
import { ProviderMark } from '../../../components/admin/ai/ProviderMark';
import type { HealthReport, ProviderCheck, ProviderHealth } from '../../../types/aiOps';
import {
  capabilityLabel,
  effectiveStatus,
  failingSince,
  featureLabel,
  formatLatency,
  headlineFor,
  orderChecks,
  statusLabel,
  statusTone,
  timeAgo,
  timeUntil,
} from '../format';
import { MetaLine, Notice } from './ui';

interface ProviderStatusSectionProps {
  report: HealthReport | null;
  loading: boolean;
  checking: boolean;
  error: string | null;
  onCheck: () => void;
  className?: string;
}

/**
 * Which providers answer, for what, and since when they stopped.
 *
 * The headline is deliberately outside the rows: a research provider that
 * cannot search the web leaves monitoring collecting nothing, and that must
 * not be something an admin has to expand a row to discover.
 */
export const ProviderStatusSection: React.FC<ProviderStatusSectionProps> = ({
  report,
  loading,
  checking,
  error,
  onCheck,
  className = '',
}) => {
  const headline = report ? headlineFor(report) : null;

  return (
    <section className={className}>
      <SectionHeader
        title="Provider status"
        aside={
          <>
            {report?.checkedAt && <span className="hidden sm:inline">Checked {timeAgo(report.checkedAt)}</span>}
            <Button size="sm" variant="secondary" onClick={onCheck} loading={checking} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
              Check now
            </Button>
          </>
        }
      />

      {error && <Notice className="mb-3">{error}</Notice>}

      {headline && (
        <Notice
          className="mb-3"
          tone={headline.tone}
          icon={
            headline.kind === 'no_web_search' ? (
              <Globe className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            ) : headline.tone === 'error' ? (
              <CircleAlert className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            ) : (
              <CircleCheck className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            )
          }
        >
          <span className="block">{headline.title}</span>
          {headline.detail && <span className="mt-0.5 block text-[12px] opacity-80">{headline.detail}</span>}
        </Notice>
      )}

      <Card divided>
        {loading && !report ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3.5">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))
        ) : report && report.providers.length > 0 ? (
          report.providers.map((provider) => <ProviderRow key={provider.provider} provider={provider} />)
        ) : (
          <p className="px-4 py-8 text-center text-[13px] text-neutral-500">No providers are configured.</p>
        )}
      </Card>

      {report?.nextCheckAt && (
        <p className="mt-2 text-[12px] text-neutral-400">Checks run again on their own, next {timeUntil(report.nextCheckAt)}.</p>
      )}
    </section>
  );
};

/** A provider can be assigned to a dozen jobs; the row names a few and counts the rest */
const MAX_PILLS = 3;

const ProviderRow: React.FC<{ provider: ProviderHealth }> = ({ provider }) => {
  const assigned = (provider.assignedTo || []).filter(Boolean);
  const status = effectiveStatus(provider);
  const checks = orderChecks(provider.checks);

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-start gap-3">
        <ProviderMark name={provider.label || provider.provider} muted={!provider.configured} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="text-[14px] text-ink">{provider.label || provider.provider}</span>
            <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>
            {assigned.length > 0 && (
              <span className="flex flex-wrap gap-1.5">
                {assigned.slice(0, MAX_PILLS).map((feature) => (
                  <Pill key={String(feature)} tone="outline">
                    {featureLabel(feature)}
                  </Pill>
                ))}
                {assigned.length > MAX_PILLS && (
                  <Pill tone="outline" title={assigned.slice(MAX_PILLS).map(featureLabel).join(', ')}>
                    +{assigned.length - MAX_PILLS}
                  </Pill>
                )}
              </span>
            )}
          </div>

          <MetaLine
            className="mt-1"
            parts={[provider.configured ? 'Key saved' : 'No key saved', assigned.length === 0 && 'Not assigned to anything']}
          />

          {provider.configured && checks.length > 0 && (
            <ul className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-x-4" aria-label={`${provider.label} checks`}>
              {checks.map((check) => (
                <CheckItem key={check.capability} check={check} />
              ))}
            </ul>
          )}

          {provider.configured && checks.length === 0 && (
            <p className="mt-2 text-[12px] text-neutral-400">Never checked — run a check to see what this key can do.</p>
          )}
        </div>
      </div>
    </div>
  );
};

/** A check reads from its icon, its word and its sentence — never from colour alone */
const CheckItem: React.FC<{ check: ProviderCheck }> = ({ check }) => {
  const never = !check.checkedAt;
  const latency = formatLatency(check.latencyMs);
  const since = never ? '' : failingSince(check);

  return (
    <li className="min-w-0">
      <div className="flex items-center gap-2 text-[13px]">
        {never ? (
          <CircleDashed className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
        ) : check.ok ? (
          <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
        ) : (
          <CircleX className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
        )}
        <span className="truncate text-ink">{capabilityLabel(check.capability)}</span>
        <span className="ml-auto flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
          {never ? 'Not checked' : check.ok ? latency : 'Failed'}
        </span>
      </div>

      <div className="pl-[26px]">
        {check.model && <p className="truncate font-ui-mono text-[11.5px] text-neutral-400">{check.model}</p>}
        {!never && !check.ok && check.message && (
          <p className="mt-0.5 break-words text-[12px] text-primary-700">{check.message}</p>
        )}
        <MetaLine
          className="mt-0.5"
          parts={[
            never ? 'Never checked' : check.ok ? `Checked ${timeAgo(check.checkedAt)}` : since,
            !never && !check.ok && check.consecutiveFailures > 1 && `${check.consecutiveFailures} checks in a row`,
          ]}
        />
      </div>
    </li>
  );
};
